// src/services/stockAdjustmentService.js
// P4-6 (31 Oct 2026) - approve / reject a stock adjustment, in ONE transaction.
//
// WHY THIS FILE EXISTS
// Stock Adjustment ("Opening Balance Entry") is the only manual way to add stock.
// The approval page wrote stock, then the ledger row, then the status, as three
// separate writes with no status check. A manager or store user could reach the
// button, but the rules let only owner / SM update stock_adjustments: stock and
// ledger were written, the status was refused, the adjustment stayed Pending, and
// pressing again added the stock again. A second tab on an old list did the same
// for owner / SM. Now:
//   - approve = re-read the adjustment, check it is still pending and that stock
//     has not moved since it was submitted, then stock + ledger + status together,
//     or nothing at all
//   - approvers = ADJUSTMENT_APPROVER_ROLES, the same list as the stock_adjustments
//     update rule (isOwnerOrSM); change the rule and this list together
//   - the stock doc comes from stockDocs.js, not resolveSummaryRef(): that query has
//     no numeric-qty test, so it would take a junk doc (MED-22) as a legacy stock doc
//     and write the opening stock onto it, or refuse an item with two junk docs
// Tests: stockAdjustmentService.test.js (Jest, the real stock movement code).
//
// P4-8: createStockAdjustment() replaces the page's count + 1 number, which gave
// two people at once the same ADJ number. Each company has its own series in
// system_counters/adj_{company_id}, same pattern as generatePONumber(). The first
// use continues from the company's highest ADJ number. A failed save can leave a
// gap, never a duplicate.

import {
  collection, doc, getDoc, getDocs, query, runTransaction, serverTimestamp, where,
} from "firebase/firestore";
import { db } from "../firebase";
import { MOVEMENT_TYPES, applyStockMovementsInTransaction } from "./inventoryService";
import { isRealStockDoc, pickStockDoc, tooManyDocsReason } from "../utils/stockDocs";
import { roundQty } from "../utils/stockPlan";

export const ADJUSTMENT_APPROVER_ROLES = ["owner", "sm"];

export const canApproveAdjustment = (role) => ADJUSTMENT_APPROVER_ROLES.includes(role);

const ADJ_PREFIX = "ADJ-";

// Highest NNNN among this company's existing "ADJ-NNNN" numbers (0 if none).
const highestExistingAdjNo = async (companyId) => {
  const snap = await getDocs(query(
    collection(db, "stock_adjustments"),
    where("company_id", "==", companyId)
  ));
  let max = 0;
  snap.docs.forEach((d) => {
    const no = String(d.data().adjustment_no || "");
    if (!no.startsWith(ADJ_PREFIX)) return;
    const n = parseInt(no.slice(ADJ_PREFIX.length), 10);
    if (Number.isFinite(n) && n > max) max = n;
  });
  return max;
};

/**
 * Number + pending adjustment in ONE transaction. Stock does not change here.
 *
 * @param {Object} input
 * @param {string} input.companyId
 * @param {Object} input.adjustment  item_id, item_code, item_name, category, unit,
 *   current_stock, new_stock, difference, adjustment_type, reason, notes,
 *   submitted_by, submitted_by_name
 * @returns {Promise<string>} the new number, e.g. "ADJ-0006"
 */
export const createStockAdjustment = async ({ companyId, adjustment }) => {
  const company_id = String(companyId || "").trim();
  if (!company_id) throw new Error("company_id is required to create a stock adjustment");

  const counterRef = doc(db, "system_counters", `adj_${company_id}`);
  const adjRef = doc(collection(db, "stock_adjustments"));

  // The seed is needed once per company. It is read outside the transaction (a
  // client transaction cannot run queries), and if this read fails nothing is
  // created: numbering never silently restarts at 0001.
  const existing = await getDoc(counterRef);
  const seed = existing.exists() ? 0 : await highestExistingAdjNo(company_id);

  return runTransaction(db, async (transaction) => {
    const snap = await transaction.get(counterRef);
    const next = (snap.exists() ? Number(snap.data().last_number || 0) : seed) + 1;
    const adjustment_no = `${ADJ_PREFIX}${String(next).padStart(4, "0")}`;

    transaction.set(counterRef, {
      counter_type: "stock_adjustment",
      company_id,
      last_number:  next,
      updated_at:   serverTimestamp(),
    }, { merge: true });

    transaction.set(adjRef, {
      ...adjustment,
      company_id,
      adjustment_no,
      status:       "pending",
      submitted_at: serverTimestamp(),
      created_at:   serverTimestamp(),
    });

    return adjustment_no;
  });
};

const checkAdjustment = (snap, companyId) => {
  if (!snap.exists()) throw new Error("Adjustment not found");
  const adj = snap.data();
  if (adj.company_id !== companyId) throw new Error("This adjustment belongs to a different company");
  return adj;
};

const pendingAdjustment = (snap, companyId) => {
  const adj = checkAdjustment(snap, companyId);
  if (adj.status !== "pending") {
    throw new Error(`${adj.adjustment_no || "This adjustment"} is already ${adj.status}. Nothing was saved.`);
  }
  return adj;
};

// The item's real stock doc, or a new doc with id = item_id. Runs outside the
// transaction: a client transaction cannot run queries.
const stockRefOf = async (companyId, itemId) => {
  const snap = await getDocs(query(
    collection(db, "inventory_summary"),
    where("company_id", "==", companyId),
    where("item_id", "==", itemId)
  ));
  const docs = snap.docs.filter((d) => isRealStockDoc(d.data()));
  if (docs.length === 0) return doc(db, "inventory_summary", itemId);

  const picked = pickStockDoc(docs, itemId);
  if (!picked) throw new Error(`${tooManyDocsReason(docs.length)}. Nothing was saved.`);
  return picked.ref;
};

/**
 * @param {Object} input
 * @param {string} input.adjustmentId  stock_adjustments doc id
 * @param {string} input.companyId
 * @param {string} input.role          the approver's role
 * @param {string} input.approvedBy    uid
 * @param {string} input.approvedByName
 */
export const approveStockAdjustment = async ({ adjustmentId, companyId, role, approvedBy, approvedByName }) => {
  if (!canApproveAdjustment(role)) throw new Error("Only Owner or SM can approve a stock adjustment.");

  const adjRef = doc(db, "stock_adjustments", adjustmentId);
  const first = checkAdjustment(await getDoc(adjRef), companyId);
  const summaryRef = await stockRefOf(companyId, first.item_id);

  await runTransaction(db, async (transaction) => {
    const adj = pendingAdjustment(await transaction.get(adjRef), companyId);

    const stockSnap = await transaction.get(summaryRef);
    const liveQty = stockSnap.exists() ? roundQty(stockSnap.data().qty) : 0;
    if (liveQty !== roundQty(adj.current_stock)) {
      throw new Error(
        `Stock changed since this was submitted (was ${adj.current_stock}, now ${liveQty}) — ` +
        `reject it and enter a new one. Nothing was saved.`
      );
    }

    await applyStockMovementsInTransaction(transaction, {
      company_id: companyId,
      movements: [{
        summaryRef,
        item: {
          item_id:   adj.item_id,
          item_code: adj.item_code || "",
          item_name: adj.item_name || "",
          category:  adj.category  || "",
          unit:      adj.unit      || "nos",
        },
        stock_change: Number(adj.difference),
        last_rate:    null,
        ledger: {
          movement_type:  MOVEMENT_TYPES.ADJUSTMENT,
          qty:            Math.abs(Number(adj.difference)),
          reference_id:   adj.adjustment_no,
          reference_type: "stock_adjustment",
          approved_by:    approvedBy,
          remarks:        `${adj.reason}${adj.notes ? " | " + adj.notes : ""}`,
        },
      }],
    });

    transaction.update(adjRef, {
      status:           "approved",
      approved_by:      approvedBy,
      approved_by_name: approvedByName,
      approved_at:      serverTimestamp(),
      updated_at:       serverTimestamp(),
    });
  });
};

/**
 * @param {Object} input
 * @param {string} input.adjustmentId
 * @param {string} input.companyId
 * @param {string} input.role           the approver's role
 * @param {string} input.rejectedBy     uid
 * @param {string} input.rejectedByName
 * @param {string} input.reason         required
 */
export const rejectStockAdjustment = async ({ adjustmentId, companyId, role, rejectedBy, rejectedByName, reason }) => {
  if (!canApproveAdjustment(role)) throw new Error("Only Owner or SM can reject a stock adjustment.");
  const why = String(reason ?? "").trim();
  if (!why) throw new Error("Rejection reason required");

  const adjRef = doc(db, "stock_adjustments", adjustmentId);
  await runTransaction(db, async (transaction) => {
    pendingAdjustment(await transaction.get(adjRef), companyId);
    transaction.update(adjRef, {
      status:           "rejected",
      rejected_by:      rejectedBy,
      rejected_by_name: rejectedByName,
      rejection_reason: why,
      rejected_at:      serverTimestamp(),
      updated_at:       serverTimestamp(),
    });
  });
};
